<template>
	<v-card :class="embedded ? '' : 'ma-5'" flat>
		<v-card-text :class="embedded ? 'pa-3' : ''">
			<!-- Loading State -->
			<div v-if="loading" class="d-flex justify-center align-center py-8">
				<v-progress-circular indeterminate color="primary" />
			</div>

			<!-- Error State -->
			<v-alert
				v-else-if="error"
				type="error"
				outlined
				dense
				class="mb-0"
			>
				{{ error }}
			</v-alert>

			<!-- Content -->
			<div v-else-if="info" class="system-info-content">
				<!-- Twilio Balance - Prominent Display -->
				<div class="balance-card mb-2" :class="{ 'balance-low': isBalanceLow }">
					<div class="balance-header">
						<v-icon :color="isBalanceLow ? 'white' : 'teal'" size="28">mdi-phone</v-icon>
						<span class="balance-label">{{ $t('twilio-balance') }}</span>
					</div>
					<div class="balance-value">
						{{ formattedBalance }}
					</div>
					<div v-if="isBalanceLow" class="balance-warning">
						<v-icon small color="white">mdi-alert</v-icon>
						<span>{{ $t('low-balance-warning') }}</span>
					</div>
				</div>

				<!-- Environment Variables Section -->
				<div class="env-section mb-2">
					<div class="env-header" @click="envExpanded = !envExpanded">
						<div class="d-flex align-center">
							<v-icon color="white" size="16" class="mr-2">mdi-console</v-icon>
							<span>Environment Variables</span>
							<v-chip x-small class="ml-2" color="white" text-color="grey darken-2">
								{{ envCount }}
							</v-chip>
							<v-icon color="white" size="16" class="ml-2">{{ envExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
						</div>
						<v-btn
							x-small
							text
							color="white"
							class="copy-all-btn"
							@click.stop="copyAllEnv"
						>
							<v-icon x-small left>mdi-content-copy</v-icon>
							{{ $t('copy-all') }}
						</v-btn>
					</div>
					<div v-if="envExpanded" class="env-content">
						<div v-if="!info.env || Object.keys(info.env).length === 0" class="env-row">
							<span class="env-key">No environment variables available</span>
						</div>
						<div
							v-for="(value, key) in info.env"
							v-else
							:key="key"
							class="env-row"
						>
							<span class="env-key">{{ key }}</span>
							<div class="env-value-wrapper">
								<span
									class="env-val"
									:class="{
										'redacted': value === '<redacted>',
										'expandable-value': value !== '<redacted>',
										'text-truncate': value !== '<redacted>' && !isExpanded('env-' + key)
									}"
									:title="value"
									@click="value !== '<redacted>' && toggleExpand('env-' + key)"
								>
									{{ value || '-' }}
								</span>
								<v-btn
									v-if="value && value !== '<redacted>'"
									icon
									x-small
									class="copy-btn"
									@click.stop="copyToClipboard(value)"
								>
									<v-icon x-small>mdi-content-copy</v-icon>
								</v-btn>
							</div>
						</div>
					</div>
				</div>

				<!-- Info Sections Grid -->
				<div class="info-sections">
					<!-- GCP Section -->
					<div class="info-section gcp-section">
						<div class="section-header">
							<v-icon color="white" size="20">mdi-google-cloud</v-icon>
							<span>Google Cloud</span>
						</div>
						<div class="section-content">
							<div class="info-row">
								<span class="info-label">{{ $t('project') }}</span>
								<div class="info-value-wrapper">
									<span class="info-value">{{ info.gcp?.project || '-' }}</span>
									<v-btn
										v-if="info.gcp?.project"
										icon
										x-small
										class="copy-btn"
										@click="copyToClipboard(info.gcp.project)"
									>
										<v-icon x-small>mdi-content-copy</v-icon>
									</v-btn>
								</div>
							</div>
							<div class="info-row">
								<span class="info-label">{{ $t('environment') }}</span>
								<div class="info-value-wrapper">
									<v-chip x-small :color="envColor" dark class="env-chip">
										{{ info.gcp?.env || '-' }}
									</v-chip>
									<v-btn
										v-if="info.gcp?.env"
										icon
										x-small
										class="copy-btn"
										@click="copyToClipboard(info.gcp.env)"
									>
										<v-icon x-small>mdi-content-copy</v-icon>
									</v-btn>
								</div>
							</div>
							<div class="info-row">
								<span class="info-label">API</span>
								<div class="info-value-wrapper">
									<span
										class="info-value expandable-value"
										:class="{ 'text-truncate': !isExpanded('gcp-api') }"
										:title="info.gcp?.api"
										@click="toggleExpand('gcp-api')"
									>
										{{ info.gcp?.api || '-' }}
									</span>
									<v-btn
										v-if="info.gcp?.api"
										icon
										x-small
										class="copy-btn"
										@click.stop="copyToClipboard(info.gcp.api)"
									>
										<v-icon x-small>mdi-content-copy</v-icon>
									</v-btn>
								</div>
							</div>
							<div class="info-row">
								<span class="info-label">App</span>
								<div class="info-value-wrapper">
									<span
										class="info-value expandable-value"
										:class="{ 'text-truncate': !isExpanded('gcp-app') }"
										:title="info.gcp?.app"
										@click="toggleExpand('gcp-app')"
									>
										{{ info.gcp?.app || '-' }}
									</span>
									<v-btn
										v-if="info.gcp?.app"
										icon
										x-small
										class="copy-btn"
										@click.stop="copyToClipboard(info.gcp.app)"
									>
										<v-icon x-small>mdi-content-copy</v-icon>
									</v-btn>
								</div>
							</div>
							<div class="info-row">
								<span class="info-label">{{ $t('port') }}</span>
								<div class="info-value-wrapper">
									<span class="info-value">{{ info.gcp?.port || '-' }}</span>
									<v-btn
										v-if="info.gcp?.port"
										icon
										x-small
										class="copy-btn"
										@click="copyToClipboard(info.gcp.port)"
									>
										<v-icon x-small>mdi-content-copy</v-icon>
									</v-btn>
								</div>
							</div>
						</div>
					</div>

					<!-- Redis Section -->
					<div class="info-section redis-section">
						<div class="section-header">
							<v-icon color="white" size="20">mdi-database</v-icon>
							<span>Redis</span>
						</div>
						<div class="section-content">
							<div class="info-row">
								<span class="info-label">{{ $t('host') }}</span>
								<div class="info-value-wrapper">
									<span class="info-value">{{ info.redis?.host || '-' }}</span>
									<v-btn
										v-if="info.redis?.host"
										icon
										x-small
										class="copy-btn"
										@click="copyToClipboard(info.redis.host)"
									>
										<v-icon x-small>mdi-content-copy</v-icon>
									</v-btn>
								</div>
							</div>
							<div class="info-row">
								<span class="info-label">{{ $t('port') }}</span>
								<div class="info-value-wrapper">
									<span class="info-value">{{ info.redis?.port || '-' }}</span>
									<v-btn
										v-if="info.redis?.port"
										icon
										x-small
										class="copy-btn"
										@click="copyToClipboard(info.redis.port)"
									>
										<v-icon x-small>mdi-content-copy</v-icon>
									</v-btn>
								</div>
							</div>
							<div class="info-row">
								<span class="info-label">DB</span>
								<div class="info-value-wrapper">
									<span class="info-value">{{ info.redis?.db || '-' }}</span>
									<v-btn
										v-if="info.redis?.db"
										icon
										x-small
										class="copy-btn"
										@click="copyToClipboard(info.redis.db)"
									>
										<v-icon x-small>mdi-content-copy</v-icon>
									</v-btn>
								</div>
							</div>
							<div class="info-row">
								<span class="info-label">{{ $t('status') }}</span>
								<div class="info-value-wrapper">
									<v-chip x-small :color="redisStatusColor" dark>
										{{ info.redis?.status || '-' }}
									</v-chip>
									<v-btn
										v-if="info.redis?.status"
										icon
										x-small
										class="copy-btn"
										@click="copyToClipboard(info.redis.status)"
									>
										<v-icon x-small>mdi-content-copy</v-icon>
									</v-btn>
								</div>
							</div>
						</div>
					</div>

					<!-- Firestore Section -->
					<div class="info-section firestore-section">
						<div class="section-header">
							<v-icon color="white" size="20">mdi-firebase</v-icon>
							<span>Firestore</span>
						</div>
						<div class="section-content">
							<div class="info-row">
								<span class="info-label">{{ $t('database') }}</span>
								<div class="info-value-wrapper">
									<span class="info-value">{{ info.firestore?.database || '-' }}</span>
									<v-btn
										v-if="info.firestore?.database"
										icon
										x-small
										class="copy-btn"
										@click="copyToClipboard(info.firestore.database)"
									>
										<v-icon x-small>mdi-content-copy</v-icon>
									</v-btn>
								</div>
							</div>
							<div class="info-row collections-row">
								<span class="info-label">{{ $t('collections') }}</span>
								<span class="info-value">{{ collectionsCount }}</span>
							</div>
							<div v-if="info.firestore?.collections?.length" class="collections-list">
								<v-chip
									v-for="collection in displayedCollections"
									:key="collection"
									x-small
									outlined
									class="collection-chip mr-1 mb-1"
									@click="copyToClipboard(collection)"
								>
									{{ collection }}
								</v-chip>
								<v-chip
									v-if="hiddenCollectionsCount > 0 && !collectionsExpanded"
									x-small
									color="grey"
									dark
									class="mr-1 mb-1 expand-chip"
									@click="collectionsExpanded = true"
								>
									+{{ hiddenCollectionsCount }}
								</v-chip>
								<v-chip
									v-if="collectionsExpanded && info.firestore?.collections?.length > maxVisibleCollections"
									x-small
									color="grey"
									outlined
									class="mr-1 mb-1 expand-chip"
									@click="collectionsExpanded = false"
								>
									<v-icon x-small left>mdi-chevron-up</v-icon>
									{{ $t('less') }}
								</v-chip>
							</div>
						</div>
					</div>

					<!-- Twilio Section -->
					<div class="info-section twilio-section">
						<div class="section-header">
							<v-icon color="white" size="20">mdi-phone</v-icon>
							<span>Twilio</span>
						</div>
						<div class="section-content">
							<div class="info-row">
								<span class="info-label">{{ $t('account') }}</span>
								<div class="info-value-wrapper">
									<span
										class="info-value expandable-value"
										:class="{ 'text-truncate': !isExpanded('twilio-account') }"
										:title="info.twilio?.account"
										@click="toggleExpand('twilio-account')"
									>
										{{ info.twilio?.account || '-' }}
									</span>
									<v-btn
										v-if="info.twilio?.account"
										icon
										x-small
										class="copy-btn"
										@click.stop="copyToClipboard(info.twilio.account)"
									>
										<v-icon x-small>mdi-content-copy</v-icon>
									</v-btn>
								</div>
							</div>
							<div class="info-row">
								<span class="info-label">TwiML</span>
								<div class="info-value-wrapper">
									<span
										class="info-value expandable-value"
										:class="{ 'text-truncate': !isExpanded('twilio-twiml') }"
										:title="info.twilio?.twiml"
										@click="toggleExpand('twilio-twiml')"
									>
										{{ info.twilio?.twiml || '-' }}
									</span>
									<v-btn
										v-if="info.twilio?.twiml"
										icon
										x-small
										class="copy-btn"
										@click.stop="copyToClipboard(info.twilio.twiml)"
									>
										<v-icon x-small>mdi-content-copy</v-icon>
									</v-btn>
								</div>
							</div>
							<div class="info-row">
								<span class="info-label">{{ $t('auth') }}</span>
								<span class="info-value redacted">{{ info.twilio?.auth || '-' }}</span>
							</div>
						</div>
					</div>

					<!-- Algolia Section -->
					<div class="info-section algolia-section">
						<div class="section-header">
							<v-icon color="white" size="20">mdi-magnify</v-icon>
							<span>Algolia</span>
						</div>
						<div class="section-content">
							<div class="info-row">
								<span class="info-label">App ID</span>
								<div class="info-value-wrapper">
									<span class="info-value">{{ info.algolia?.app || '-' }}</span>
									<v-btn
										v-if="info.algolia?.app"
										icon
										x-small
										class="copy-btn"
										@click="copyToClipboard(info.algolia.app)"
									>
										<v-icon x-small>mdi-content-copy</v-icon>
									</v-btn>
								</div>
							</div>
							<div class="info-row">
								<span class="info-label">{{ $t('key') }}</span>
								<span class="info-value redacted">{{ info.algolia?.key || '-' }}</span>
							</div>
						</div>
					</div>

					<!-- Gmail Section -->
					<div class="info-section gmail-section">
						<div class="section-header">
							<v-icon color="white" size="20">mdi-gmail</v-icon>
							<span>Gmail</span>
						</div>
						<div class="section-content">
							<div class="info-row">
								<span class="info-label">Client ID</span>
								<div class="info-value-wrapper">
									<span
										class="info-value expandable-value"
										:class="{ 'text-truncate': !isExpanded('gmail-clientId') }"
										:title="info.email?.gmail?.clientId"
										@click="toggleExpand('gmail-clientId')"
									>
										{{ info.email?.gmail?.clientId || '-' }}
									</span>
									<v-btn
										v-if="info.email?.gmail?.clientId"
										icon
										x-small
										class="copy-btn"
										@click.stop="copyToClipboard(info.email.gmail.clientId)"
									>
										<v-icon x-small>mdi-content-copy</v-icon>
									</v-btn>
								</div>
							</div>
							<div class="info-row">
								<span class="info-label">Secret</span>
								<span class="info-value redacted">{{ info.email?.gmail?.clientSecret || '-' }}</span>
							</div>
							<div class="info-row">
								<span class="info-label">Redirect</span>
								<div class="info-value-wrapper">
									<span
										class="info-value expandable-value"
										:class="{ 'text-truncate': !isExpanded('gmail-redirect') }"
										:title="info.email?.gmail?.redirectUrl"
										@click="toggleExpand('gmail-redirect')"
									>
										{{ info.email?.gmail?.redirectUrl || '-' }}
									</span>
									<v-btn
										v-if="info.email?.gmail?.redirectUrl"
										icon
										x-small
										class="copy-btn"
										@click.stop="copyToClipboard(info.email.gmail.redirectUrl)"
									>
										<v-icon x-small>mdi-content-copy</v-icon>
									</v-btn>
								</div>
							</div>
							<div class="info-row scope-row">
								<span class="info-label">Scope</span>
								<div class="info-value-wrapper">
									<div
										class="info-value expandable-value scope-value"
										:class="{ 'text-truncate': !isExpanded('gmail-scope') }"
										:title="info.email?.gmail?.scope"
										@click="toggleExpand('gmail-scope')"
									>
										<template v-if="isExpanded('gmail-scope') && info.email?.gmail?.scope">
											<div v-for="(s, i) in formatGmailScope(info.email.gmail.scope)" :key="i" class="scope-item">
												{{ s }}
											</div>
										</template>
										<template v-else>
											{{ info.email?.gmail?.scope || '-' }}
										</template>
									</div>
									<v-btn
										v-if="info.email?.gmail?.scope"
										icon
										x-small
										class="copy-btn"
										@click.stop="copyToClipboard(info.email.gmail.scope)"
									>
										<v-icon x-small>mdi-content-copy</v-icon>
									</v-btn>
								</div>
							</div>
							<div class="info-row">
								<span class="info-label">Topic</span>
								<div class="info-value-wrapper">
									<span
										class="info-value expandable-value"
										:class="{ 'text-truncate': !isExpanded('gmail-topic') }"
										:title="info.email?.gmail?.topic"
										@click="toggleExpand('gmail-topic')"
									>
										{{ info.email?.gmail?.topic || '-' }}
									</span>
									<v-btn
										v-if="info.email?.gmail?.topic"
										icon
										x-small
										class="copy-btn"
										@click.stop="copyToClipboard(info.email.gmail.topic)"
									>
										<v-icon x-small>mdi-content-copy</v-icon>
									</v-btn>
								</div>
							</div>
						</div>
					</div>

					<!-- Outlook Section -->
					<div class="info-section outlook-section">
						<div class="section-header">
							<v-icon color="white" size="20">mdi-microsoft-outlook</v-icon>
							<span>Outlook</span>
						</div>
						<div class="section-content">
							<div class="info-row">
								<span class="info-label">Client ID</span>
								<div class="info-value-wrapper">
									<span
										class="info-value expandable-value"
										:class="{ 'text-truncate': !isExpanded('outlook-clientId') }"
										:title="info.email?.outlook?.clientId"
										@click="toggleExpand('outlook-clientId')"
									>
										{{ info.email?.outlook?.clientId || '-' }}
									</span>
									<v-btn
										v-if="info.email?.outlook?.clientId"
										icon
										x-small
										class="copy-btn"
										@click.stop="copyToClipboard(info.email.outlook.clientId)"
									>
										<v-icon x-small>mdi-content-copy</v-icon>
									</v-btn>
								</div>
							</div>
							<div class="info-row">
								<span class="info-label">Secret</span>
								<span class="info-value redacted">{{ info.email?.outlook?.clientSecret || '-' }}</span>
							</div>
							<div class="info-row">
								<span class="info-label">Redirect</span>
								<div class="info-value-wrapper">
									<span
										class="info-value expandable-value"
										:class="{ 'text-truncate': !isExpanded('outlook-redirect') }"
										:title="info.email?.outlook?.redirectUrl"
										@click="toggleExpand('outlook-redirect')"
									>
										{{ info.email?.outlook?.redirectUrl || '-' }}
									</span>
									<v-btn
										v-if="info.email?.outlook?.redirectUrl"
										icon
										x-small
										class="copy-btn"
										@click.stop="copyToClipboard(info.email.outlook.redirectUrl)"
									>
										<v-icon x-small>mdi-content-copy</v-icon>
									</v-btn>
								</div>
							</div>
							<div class="info-row scope-row">
								<span class="info-label">Scope</span>
								<div class="info-value-wrapper">
									<div
										class="info-value expandable-value scope-value"
										:class="{ 'text-truncate': !isExpanded('outlook-scope') }"
										:title="info.email?.outlook?.scope"
										@click="toggleExpand('outlook-scope')"
									>
										<template v-if="isExpanded('outlook-scope') && info.email?.outlook?.scope">
											<div v-for="(s, i) in formatOutlookScope(info.email.outlook.scope)" :key="i" class="scope-item">
												{{ s }}
											</div>
										</template>
										<template v-else>
											{{ info.email?.outlook?.scope || '-' }}
										</template>
									</div>
									<v-btn
										v-if="info.email?.outlook?.scope"
										icon
										x-small
										class="copy-btn"
										@click.stop="copyToClipboard(info.email.outlook.scope)"
									>
										<v-icon x-small>mdi-content-copy</v-icon>
									</v-btn>
								</div>
							</div>
						</div>
					</div>

					<!-- GCS Section -->
					<div class="info-section gcs-section">
						<div class="section-header">
							<v-icon color="white" size="20">mdi-bucket</v-icon>
							<span>Cloud Storage</span>
						</div>
						<div class="section-content">
							<div class="info-row">
								<span class="info-label">{{ $t('bucket') }}</span>
								<div class="info-value-wrapper">
									<span class="info-value">{{ info.gcs?.bucket || '-' }}</span>
									<v-btn
										v-if="info.gcs?.bucket"
										icon
										x-small
										class="copy-btn"
										@click="copyToClipboard(info.gcs.bucket)"
									>
										<v-icon x-small>mdi-content-copy</v-icon>
									</v-btn>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</v-card-text>
	</v-card>
</template>

<script>
export default {
	name: "SystemInfo",

	props: {
		embedded: {
			type: Boolean,
			default: false
		}
	},

	data() {
		return {
			loading: false,
			error: null,
			info: null,
			maxVisibleCollections: 8,
			collectionsExpanded: false,
			expandedFields: {},
			envExpanded: false
		};
	},

	computed: {
		envCount() {
			return this.info?.env ? Object.keys(this.info.env).length : 0;
		},

		formattedBalance() {
			const balance = this.info?.twilio?.balance?.balance;

			if (balance === undefined || balance === null) return "-";
			return new Intl.NumberFormat("en-US", {
				style: "currency",
				currency: "USD"
			}).format(parseFloat(balance));
		},

		isBalanceLow() {
			const balance = parseFloat(this.info?.twilio?.balance?.balance);

			return !isNaN(balance) && balance < 100;
		},

		envColor() {
			const env = this.info?.gcp?.env?.toLowerCase();

			if (env === "production") return "red";
			if (env === "development") return "blue";
			if (env === "local") return "grey";
			return "purple";
		},

		redisStatusColor() {
			const status = this.info?.redis?.status?.toLowerCase();

			if (status === "ready") return "success";
			if (status === "connecting") return "warning";
			return "error";
		},

		collectionsCount() {
			return this.info?.firestore?.collections?.length || 0;
		},

		visibleCollections() {
			return this.info?.firestore?.collections?.slice(0, this.maxVisibleCollections) || [];
		},

		displayedCollections() {
			if (this.collectionsExpanded) {
				return this.info?.firestore?.collections || [];
			}
			return this.visibleCollections;
		},

		hiddenCollectionsCount() {
			const total = this.info?.firestore?.collections?.length || 0;

			return Math.max(0, total - this.maxVisibleCollections);
		}
	},

	created() {
		this.fetchInfo();
	},

	methods: {
		async fetchInfo(options) {
			const silent = options?.silent || false;
			let success = true;

			if (!silent) {
				this.loading = true;
				this.error = null;
			}

			try {
				const { data } = await this.axios.get("/devops");

				this.info = data;
			} catch (err) {
				console.error("Failed to fetch system info:", err);
				success = false;
				if (!silent) this.error = this.$t("failed-to-load-system-info");
			} finally {
				if (!silent) this.loading = false;
			}

			return { success };
		},

		async copyToClipboard(value) {
			if (!value) return;
			try {
				await navigator.clipboard.writeText(String(value));
				this.$success(this.$t("copied-to-clipboard"));
			} catch (err) {
				console.error("Failed to copy:", err);
			}
		},

		toggleExpand(fieldId) {
			this.$set(this.expandedFields, fieldId, !this.expandedFields[fieldId]);
		},

		isExpanded(fieldId) {
			return !!this.expandedFields[fieldId];
		},

		async copyAllEnv() {
			if (!this.info?.env) return;
			const envString = Object.entries(this.info.env)
				.map(([key, value]) => `${key}=${value}`)
				.join("\n");

			try {
				await navigator.clipboard.writeText(envString);
				this.$success(this.$t("copied-to-clipboard"));
			} catch (err) {
				console.error("Failed to copy:", err);
			}
		},

		formatGmailScope(scope) {
			if (!scope) return [];
			return scope.split(" ").filter(s => s.trim());
		},

		formatOutlookScope(scope) {
			if (!scope) return [];
			return scope.split(",").map(s => s.trim()).filter(s => s);
		}
	}
};
</script>

<style scoped>
	.system-info-content {
		display: flex;
		flex-direction: column;
		max-height: 500px;
		overflow-y: auto;
	}

	@media (max-width: 599px) {
		.system-info-content {
			max-height: none;
		}
	}

	/* Balance Card */
	.balance-card {
		background: linear-gradient(135deg, #00897b 0%, #004d40 100%);
		border-radius: 10px;
		padding: 12px;
		color: white;
		text-align: center;
		box-shadow: 0 4px 20px rgba(0, 137, 123, 0.3);
	}

	.balance-card.balance-low {
		background: linear-gradient(135deg, #f44336 0%, #c62828 100%);
		box-shadow: 0 4px 20px rgba(244, 67, 54, 0.4);
		animation: pulse-warning 2s infinite;
	}

	@keyframes pulse-warning {
		0%, 100% {
			box-shadow: 0 4px 20px rgba(244, 67, 54, 0.4);
		}
		50% {
			box-shadow: 0 4px 30px rgba(244, 67, 54, 0.6);
		}
	}

	.balance-header {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 6px;
		margin-bottom: 4px;
	}

	.balance-label {
		font-size: 12px;
		font-weight: 500;
		opacity: 0.9;
	}

	.balance-value {
		font-size: 28px;
		font-weight: 700;
		letter-spacing: -1px;
	}

	.balance-warning {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 6px;
		margin-top: 6px;
		font-size: 11px;
		opacity: 0.9;
	}

	/* Info Sections Grid */
	.info-sections {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
		gap: 10px;
		flex-shrink: 0;
	}

	.info-section {
		border-radius: 8px;
		overflow: hidden;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
	}

	.section-header {
		display: flex;
		align-items: center;
		gap: 8px;
		padding: 6px 10px;
		font-weight: 600;
		font-size: 11px;
		color: white;
	}

	/* Section Colors */
	.gcp-section .section-header {
		background: linear-gradient(135deg, #4285f4 0%, #3367d6 100%);
	}

	.redis-section .section-header {
		background: linear-gradient(135deg, #dc382d 0%, #b52d24 100%);
	}

	.firestore-section .section-header {
		background: linear-gradient(135deg, #ffca28 0%, #f9a825 100%);
	}

	.twilio-section .section-header {
		background: linear-gradient(135deg, #f22f46 0%, #c7243a 100%);
	}

	.algolia-section .section-header {
		background: linear-gradient(135deg, #5468ff 0%, #3c4fe0 100%);
	}

	.gmail-section .section-header {
		background: linear-gradient(135deg, #ea4335 0%, #c5221f 100%);
	}

	.outlook-section .section-header {
		background: linear-gradient(135deg, #0078d4 0%, #005a9e 100%);
	}

	.gcs-section .section-header {
		background: linear-gradient(135deg, #607d8b 0%, #455a64 100%);
	}

	.section-content {
		padding: 8px 10px;
		background: #fafafa;
	}

	.info-row {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 4px 0;
		border-bottom: 1px solid #eee;
	}

	.info-row:last-child {
		border-bottom: none;
	}

	.info-label {
		font-size: 11px;
		color: #666;
		font-weight: 500;
		flex-shrink: 0;
		margin-right: 8px;
		min-width: 55px;
	}

	.info-value-wrapper {
		display: flex;
		align-items: center;
		gap: 4px;
		min-width: 0;
		justify-content: flex-end;
		flex: 1;
	}

	.info-value {
		font-size: 12px;
		color: #333;
		font-weight: 500;
		text-align: right;
		max-width: 220px;
	}

	.expandable-value {
		cursor: pointer;
		transition: background-color 0.2s, max-width 0.2s;
	}

	.expandable-value:hover {
		background-color: rgba(0, 0, 0, 0.04);
		border-radius: 2px;
	}

	.expandable-value:not(.text-truncate) {
		max-width: none;
		word-break: break-all;
	}

	/* Scope display */
	.scope-row {
		align-items: flex-start;
	}

	.scope-value {
		display: flex;
		flex-direction: column;
		align-items: flex-end;
	}

	.scope-value.text-truncate {
		display: block;
	}

	.scope-item {
		font-family: monospace;
		font-size: 11px;
		padding: 2px 0;
		word-break: break-all;
	}

	.copy-btn {
		opacity: 0.4;
		transition: opacity 0.2s;
		flex-shrink: 0;
	}

	.copy-btn:hover {
		opacity: 1;
	}

	.info-row:hover .copy-btn {
		opacity: 0.7;
	}

	.info-value.redacted {
		color: #e53935;
		font-style: italic;
	}

	.api-url {
		font-family: monospace;
		font-size: 11px;
	}

	.env-chip {
		font-weight: 600;
		text-transform: uppercase;
		font-size: 10px !important;
	}

	/* Collections */
	.collections-row {
		border-bottom: none !important;
		padding-bottom: 4px;
	}

	.collections-list {
		display: flex;
		flex-wrap: wrap;
		padding-top: 4px;
	}

	.collection-chip {
		font-size: 10px !important;
		height: 20px !important;
		cursor: pointer;
	}

	.collection-chip:hover {
		background: rgba(0, 0, 0, 0.08) !important;
	}

	.expand-chip {
		cursor: pointer;
	}

	.expand-chip:hover {
		opacity: 0.8;
	}

	/* Environment Variables */
	.env-section {
		border-radius: 6px;
		border: 1px solid #e0e0e0;
		min-height: 36px;
		background: white;
		flex-shrink: 0;
		overflow: visible;
	}

	.env-header {
		background: linear-gradient(135deg, #455a64 0%, #37474f 100%);
		color: white;
		padding: 6px 10px;
		font-weight: 600;
		font-size: 11px;
		display: flex;
		justify-content: space-between;
		align-items: center;
		transition: background 0.2s;
		cursor: pointer;
		min-height: 36px;
		border-radius: 6px 6px 0 0;
	}

	.env-section:not(:has(.env-content)) .env-header {
		border-radius: 6px;
	}

	.copy-all-btn {
		opacity: 0.8;
		text-transform: none;
	}

	.copy-all-btn:hover {
		opacity: 1;
		background: rgba(255, 255, 255, 0.1);
	}

	.env-header:hover {
		background: linear-gradient(135deg, #546e7a 0%, #455a64 100%);
	}

	.env-content {
		max-height: 250px;
		overflow-y: auto;
		background: #fafafa;
		border-top: 1px solid #e0e0e0;
		border-radius: 0 0 6px 6px;
		display: block;
	}

	.env-row {
		display: flex;
		justify-content: space-between;
		align-items: flex-start;
		padding: 4px 10px;
		border-bottom: 1px solid #eee;
		font-size: 10px;
		gap: 10px;
	}

	.env-row:last-child {
		border-bottom: none;
	}

	.env-row:hover {
		background: #f5f5f5;
	}

	.env-key {
		font-family: monospace;
		font-weight: 600;
		color: #1565c0;
		flex-shrink: 0;
		max-width: 200px;
		word-break: break-all;
	}

	.env-value-wrapper {
		display: flex;
		align-items: flex-start;
		gap: 4px;
		min-width: 0;
		flex: 1;
		justify-content: flex-end;
	}

	.env-val {
		font-family: monospace;
		color: #333;
		text-align: right;
		max-width: 350px;
	}

	.env-val.redacted {
		color: #e53935;
		font-style: italic;
	}

	.env-val.expandable-value:not(.text-truncate) {
		max-width: none;
		word-break: break-all;
	}

	/* Responsive */
	@media (max-width: 600px) {
		.info-sections {
			grid-template-columns: 1fr;
		}

		.balance-value {
			font-size: 28px;
		}

		.env-key {
			max-width: 120px;
		}

		.env-val {
			max-width: 150px;
		}
	}
</style>
